import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Inbox, CheckCircle2, RefreshCw, Filter, ChevronRight } from "lucide-react";

const KIND_ICONS = {
  closure_verification: "🟢",
  parts_approval: "🔧",
  happy_call: "📞",
  final_closure: "✅",
  pna_source: "📦",
  product_review: "🆕",
  parts_dispatch: "🚚",
  parts_returns: "↩️",
};

export default function MyApprovals() {
  const nav = useNavigate();
  const [summary, setSummary] = useState({ total: 0, queues: [] });
  const [activeKind, setActiveKind] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadSummary = () => api.get("/my-approvals/summary").then((r) => {
    setSummary(r.data);
    if (!activeKind && r.data.queues?.length) {
      // Pick the first non-zero queue if any, else the first queue
      const firstNonZero = r.data.queues.find((q) => q.count > 0);
      setActiveKind((firstNonZero || r.data.queues[0]).kind);
    }
  });

  const loadRows = (kind, q = {}) => {
    if (!kind) return;
    setLoadingRows(true);
    const params = { kind };
    if (q.search) params.search = q.search;
    if (q.date_from) params.date_from = q.date_from;
    if (q.date_to) params.date_to = q.date_to;
    api.get("/my-approvals/list", { params })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoadingRows(false));
  };

  useEffect(() => { loadSummary(); }, []); // eslint-disable-line
  useEffect(() => {
    if (activeKind) loadRows(activeKind, { search, date_from: dateFrom, date_to: dateTo });
  }, [activeKind]); // eslint-disable-line

  const applyFilters = () => loadRows(activeKind, { search, date_from: dateFrom, date_to: dateTo });
  const clearFilters = () => { setSearch(""); setDateFrom(""); setDateTo(""); loadRows(activeKind); };

  const openRow = (row) => {
    if (row.deep_link_kind === "complaint") nav(`/complaints/${row.deep_link_id}`);
    else if (row.deep_link_kind === "product") nav(`/product-approval`);
    else if (row.deep_link_kind === "parts_return") nav(`/complaints/${row.deep_link_id}`);
  };

  const activeQ = summary.queues.find((q) => q.kind === activeKind);

  return (
    <div className="space-y-5" data-testid="my-approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">My Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pending items waiting on your decision. Total:{" "}
            <span className="font-semibold text-slate-900" data-testid="my-approvals-total">{summary.total}</span>
          </p>
        </div>
        <Button variant="outline" onClick={() => { loadSummary(); applyFilters(); }} data-testid="refresh-approvals-btn">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {summary.queues.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-400 text-sm">
          You have no approval responsibilities assigned to your role.
        </CardContent></Card>
      ) : (
        <>
          {/* Queue tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="queue-tabs">
            {summary.queues.map((q) => {
              const isActive = q.kind === activeKind;
              return (
                <button key={q.kind}
                        onClick={() => setActiveKind(q.kind)}
                        className={`text-left p-3 rounded-md border transition ${isActive ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                        data-testid={`queue-tab-${q.kind}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[11px] uppercase tracking-wider font-semibold ${isActive ? "text-slate-300" : "text-slate-500"}`}>{KIND_ICONS[q.kind] || "•"} {q.kind.replace(/_/g, " ")}</span>
                    <Badge variant="outline" className={isActive ? "bg-white text-slate-900 border-white" : ""} data-testid={`queue-count-${q.kind}`}>
                      {q.count}
                    </Badge>
                  </div>
                  <div className={`text-sm font-semibold mt-1 ${isActive ? "text-white" : "text-slate-900"}`}>{q.label}</div>
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <Card><CardContent className="p-4 flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-slate-600">Search</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="complaint #, customer, model, dealer…"
                      data-testid="my-approvals-search" />
            </div>
            <div>
              <label className="text-xs text-slate-600">From (AD)</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                      className="border border-slate-200 rounded-md h-9 px-2 text-sm bg-white block"
                      data-testid="my-approvals-date-from" />
            </div>
            <div>
              <label className="text-xs text-slate-600">To (AD)</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                      className="border border-slate-200 rounded-md h-9 px-2 text-sm bg-white block"
                      data-testid="my-approvals-date-to" />
            </div>
            <Button variant="outline" onClick={applyFilters} data-testid="apply-filters-btn">
              <Filter size={14} className="mr-1" /> Apply
            </Button>
            <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">Clear</Button>
          </CardContent></Card>

          {/* Rows */}
          <Card><CardContent className="p-0">
            {loadingRows ? (
              <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center gap-2"
                   data-testid="empty-queue">
                <CheckCircle2 size={32} className="text-emerald-400" />
                <div>Nothing pending in "{activeQ?.label || activeKind}". You're all caught up!</div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <div key={row.id}
                       className="p-4 flex items-start gap-3 hover:bg-slate-50 cursor-pointer"
                       onClick={() => openRow(row)}
                       data-testid={`approval-row-${row.id}`}>
                    <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                      <Inbox size={14} />
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                      <div className="font-mono text-xs text-blue-700">
                        {row.complaint_number || row.product_name || `#${row.id?.slice(-6)}`}
                      </div>
                      <div className="text-xs text-slate-500 md:text-right">
                        Pending {row.pending_days ?? "?"} day{row.pending_days === 1 ? "" : "s"}
                      </div>
                      <div className="text-sm text-slate-900">
                        {row.customer_name || "—"}
                        {row.brand && <span className="text-slate-500"> · {row.brand}</span>}
                        {row.product_model_number && <span className="text-slate-500"> · {row.product_model_number}</span>}
                      </div>
                      <div className="text-xs text-slate-600 md:text-right">
                        {row.dealer_name || ""}
                        {row.technician_name && <span> · 🔧 {row.technician_name}</span>}
                      </div>
                      {row.part_name && (
                        <div className="text-xs text-slate-700 md:col-span-2">
                          {row.part_name} {row.part_code ? `(${row.part_code})` : ""} × {row.qty} — {row.condition}
                        </div>
                      )}
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wider font-semibold w-fit">
                        {row.pending_action}
                      </Badge>
                    </div>
                    <ChevronRight size={16} className="text-slate-300 mt-1 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
